import React, { useEffect, useMemo, useState } from "react";
import { notifyError } from "../../utils/tostfy";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { handleApiCall } from "../../apis";
import { endpoint } from "../../utils/endpoints";
import { formatDateTime as formatDateTimeDisplay } from "../../utils/formatDateTime";
import { formatInr, truncateAmountParts } from "../../utils/formatAmount";
import {
  buildFuelFormImageMeta,
  FuelImageMetadataOverlay,
  FuelImageMetadataPanel,
} from "../../Components/FuelImageMetadata/FuelImageMetadata";
const pad2 = (n) => String(n).padStart(2, "0");
const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return formatDateTimeDisplay(d);
};

const formatDateOnly = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const toTitleStatus = (status) => {
  if (status === 1) return "Active";
  if (status === 0) return "Inactive";
  if (status === 4) return "Completed";
  if (typeof status === "number") return String(status);
  return "—";
};

const getAssetBaseUrl = () => {
  const api = process.env.REACT_APP_API || "";
  try {
    const u = new URL(api);
    return `${u.protocol}//${u.host}`;
  } catch {
    return "";
  }
};

const resolveImageUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = getAssetBaseUrl();
  if (!base) return path;
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
};

/** API may return the form at root or under a single key */
const unwrapFuelFormPayload = (raw) => {
  if (!raw || typeof raw !== "object") return raw;
  return (
    raw.fuel_form_data ||
    raw.fuel_form ||
    raw.fuelForm ||
    raw.form_data ||
    raw.data ||
    raw
  );
};

const pickPreviousOdometer = (d) => {
  if (!d || typeof d !== "object") return null;
  const veh = d.vehicle_id;
  if (typeof veh === "object" && veh != null) {
    const v =
      veh.odometer_reading ??
      veh.last_odometer_reading ??
      veh.previous_odometer_reading;
    if (v != null && v !== "" && !Number.isNaN(Number(v))) return Number(v);
  }
  const fallbacks = [
    d.previous_odometer_reading,
    d.last_odometer_reading,
    d.start_odometer_reading,
    d.vehicle_odometer_reading,
    d.old_odometer_reading,
    typeof d.vehicle === "object" && d.vehicle != null
      ? d.vehicle.odometer_reading ?? d.vehicle.last_odometer_reading
      : null,
    typeof d.vehicle_details === "object" && d.vehicle_details != null
      ? d.vehicle_details.odometer_reading ?? d.vehicle_details.last_odometer_reading
      : null,
  ];
  for (const f of fallbacks) {
    if (f != null && f !== "" && !Number.isNaN(Number(f))) return Number(f);
  }
  return null;
};

const computeDistanceKmLabel = (d) => {
  const curr = d?.odometer_reading;
  const prev = pickPreviousOdometer(d);
  if (curr == null || curr === "" || Number.isNaN(Number(curr)) || prev == null) {
    return "—";
  }
  const diff = Number(curr) - Number(prev);
  if (!Number.isFinite(diff)) return "—";
  const parts = truncateAmountParts(diff);
  if (!parts) return "—";
  return `${parts.sign}${parts.intPart}.${parts.fraction} km`;
};

const mapFuelFormDetail = (d) => {
  if (!d || typeof d !== "object") return null;

  const veh = d?.vehicle_id;
  const distanceKm = computeDistanceKmLabel(d);
  const effRaw = d?.efficency ?? d?.efficiency;
  const efficiencyDisplay =
    effRaw != null && effRaw !== "" && !Number.isNaN(Number(effRaw))
      ? `${Number(effRaw).toLocaleString("en-IN", { maximumFractionDigits: 2 })} KM/L`
      : "—";

  const imageMeta = buildFuelFormImageMeta(d, formatDateTime);

  return {
    date: formatDateOnly(d.createdAt),
    dateTime: formatDateTime(d.createdAt),
    vehicle: (typeof veh === "object" && (veh?.registration_number || veh?.name)) || "—",
    chassis: (typeof veh === "object" && veh?.chassis_number) || "—",
    fuelFilled: d?.fuel_quantity != null ? `${Number(d.fuel_quantity).toLocaleString("en-IN")} L` : "—",
    fuelOperatorName: d?.fuel_operator_id?.full_name || "—",
    fuelOperatorEmployeeId: d?.fuel_operator_id?.employee_id || "—",
    miplLocation: d?.fuel_pump_id?.mipl_location_id?.name || "—",
    fuelPumpName: d?.fuel_pump_id?.fuel_pump_name || d?.fuel_pump_id?.name || "—",
    odometerReading:
      d?.odometer_reading != null ? `${Number(d.odometer_reading).toLocaleString("en-IN")} KM` : "—",
    efficiency: efficiencyDisplay,
    fuelAmount: formatInr(d?.fuel_amount),
    distanceKm,
    latitude: imageMeta.latitude || "—",
    longitude: imageMeta.longitude || "—",
    address: imageMeta.address || "—",
    status: toTitleStatus(d?.status),
    images: [
      { label: "Odometer Reading Image", src: resolveImageUrl(d?.odometer_reading_image), ...imageMeta },
      { label: "Truck Front Image", src: resolveImageUrl(d?.vehicle_front_image), ...imageMeta },
      { label: "Before Fueling Image", src: resolveImageUrl(d?.before_fueling_image), ...imageMeta },
      { label: "After Fueling Image", src: resolveImageUrl(d?.after_fueling_image), ...imageMeta },
    ],
  };
};

const ViewFuelDetails = () => {
  const { setSubheading } = useOutletContext();
  const [searchParams] = useSearchParams();
  const fuelFormId = (
    searchParams.get("fuel_form_id") ||
    searchParams.get("fuel_fill_id") ||
    ""
  ).trim();
  const fuelOperatorId = (searchParams.get("fuel_operator_id") || "").trim();

  const backToOperatorView =
    fuelOperatorId !== ""
      ? `/fuel-operator-management/view?fuel_operator_id=${encodeURIComponent(fuelOperatorId)}`
      : "/fuel-operator-management";

  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    setSubheading("Fuel Details");
    return () => {
      setSubheading("");
    };
  }, [setSubheading]);

  useEffect(() => {
    const load = async () => {
      if (!fuelFormId) {
        setLoading(false);
        setDetail(null);
        return;
      }
      setLoading(true);
      try {
        const response = await handleApiCall(
          "get",
          `${endpoint.viewFuelHistory}?fuel_form_id=${encodeURIComponent(fuelFormId)}`,
          {}
        );
        if (response?.data?.code === 200) {
          const raw = unwrapFuelFormPayload(response?.data?.data);
          setDetail(mapFuelFormDetail(raw));
        } else {
          setDetail(null);
          const errMsg =
            response?.response?.data?.errorMessage ||
            response?.response?.data?.message ||
            response?.data?.message ||
            "Failed to load fuel details";
          notifyError(errMsg);
        }
      } catch {
        setDetail(null);
        notifyError("Failed to load fuel details");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [fuelFormId]);

  const imageData = useMemo(
    () => (detail?.images || []).filter((img) => Boolean(img.src)),
    [detail]
  );

  const Card = ({ children }) => (
    <div className="bg-white rounded-2xl border md:p-6 px-3 py-2 mb-4 font-UbuntuMedium">{children}</div>
  );

  const Row = ({ label, value }) => (
    <div className="flex justify-between gap-3 py-1">
      <span className="text-gray-400">{label}</span>
      <span className="text-gray-800 text-right break-words">{value || "—"}</span>
    </div>
  );

  if (!fuelFormId && !loading) {
    return (
      <>
        <div className="mt-7 px-1">
          <p className="text-gray-500 mb-3">Missing fuel form id. Open details from the fuel history list.</p>
          <Link to={backToOperatorView} className="btn btn-primary">
            Back
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="md:mt-7 mt-3">
        <div className="flex items-center mb-3">
          <h3 className="md:text-xl font-UbuntuMedium my-0">Fuel Details</h3>
          <Link to={backToOperatorView} className="btn btn-primary ml-auto">
            Back
          </Link>
        </div>

        {loading ? (
          <div className="bg-white shadow-sm rounded-md p-6 mb-4 text-center text-gray-500">Loading...</div>
        ) : detail ? (
          <>
            <div className="rounded-lg md:px-4 md:py-2 bg-white md:mb-4 mb-2 shadow-sm">
              <Card>
                <div className="grid grid-cols-1 md:grid-cols-2 md:gap-12 max-[767px]:text-sm">
                  <div>
                    <Row label="Date" value={detail.date} />
                    <Row label="Fuel Operator Name" value={detail.fuelOperatorName} />
                    <Row label="Fuel Operator Employee ID" value={detail.fuelOperatorEmployeeId} />
                   
                    <Row label="Fuel Pump Name" value={detail.fuelPumpName} />
                    <Row label="Vehicle" value={detail.vehicle} />
                    <Row label="Odometer Reading" value={detail.odometerReading} />
                    
                  </div>
                  <div>
                  <Row label="Fuel Quantity (Liters)" value={detail.fuelFilled} />
                  <Row label="Distance Travel (KM)" value={detail.distanceKm} />
                    {/* <Row label="Chassis No." value={detail.chassis} /> */}
                    <Row label="Efficiency" value={detail.efficiency} />
                 
                    <Row label="Fuel Amount" value={detail.fuelAmount} />
                  </div>
                </div>
              </Card>
              {/* <Card>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                  <div>
                    <Row label="Fuel Operator Name" value={detail.fuelOperatorName} />
                    <Row label="MIPL Location" value={detail.miplLocation} />
                    <Row label="Status" value={detail.status} />
                  </div>
                  <div>
                    <Row label="Address" value={detail.address} />
                    <Row label="Latitude" value={detail.latitude} />
                    <Row label="Longitude" value={detail.longitude} />
                  </div>
                </div>
              </Card> */}
            </div>

            <div className="mb-4 max-w-screen-md rounded-lg bg-white md:p-4 p-3 shadow-sm">
              <p className="mb-1 text-sm font-UbuntuMedium text-gray-500">Address</p>
              <p className="mb-0 text-sm leading-relaxed text-gray-800">{detail.address}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 md:gap-5 gap-3 max-w-screen-xl">
              {imageData.length === 0 ? (
                <div className="sm:col-span-2 xl:col-span-4 text-gray-500 text-sm">No images available</div>
              ) : (
                imageData.map((item) => (
                  <div
                    key={item.label}
                    onClick={() => setSelectedImage(item)}
                    className="cursor-pointer"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && setSelectedImage(item)}
                  >
                    <div className="group relative h-52 overflow-hidden rounded-2xl bg-gray-200 shadow-sm ring-1 ring-black/5 transition hover:shadow-md">
                      <img
                        src={item.src}
                        alt={item.label}
                        className="h-full w-full object-cover transition duration-200 group-hover:scale-[1.02]"
                      />
                      <FuelImageMetadataOverlay
                        capturedAt={item.capturedAt}
                        latitude={item.latitude}
                        longitude={item.longitude}
                        address={item.address}
                      />
                    </div>
                    <p className="mt-2 text-center text-base font-UbuntuMedium text-gray-800">{item.label}</p>
                  </div>
                ))
              )}
            </div>
          </>
        ) : (
          <div className="bg-white shadow-sm rounded-md p-6 mb-4 text-center text-gray-500">
            Could not load fuel details.
          </div>
        )}

        {selectedImage ? (
          <div className="fixed inset-0 bg-black/70 flex justify-center items-center z-[9999] p-4">
            <div className="inline-flex max-h-[90vh] max-w-[92vw] flex-col overflow-hidden rounded-xl bg-transparent shadow-2xl">
              <div className="relative bg-transparent">
                <button
                  type="button"
                  onClick={() => setSelectedImage(null)}
                  className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-white/70 bg-white/95 text-xl font-bold leading-none text-gray-800 shadow-md transition hover:bg-white"
                  aria-label="Close image preview"
                >
                  ×
                </button>
                <img
                  src={selectedImage.src}
                  alt={selectedImage.label || "Full preview"}
                  className="block max-h-[72vh] max-w-[92vw] rounded-t-xl object-contain"
                />
              </div>
              <FuelImageMetadataPanel
                capturedAt={selectedImage.capturedAt}
                latitude={selectedImage.latitude}
                longitude={selectedImage.longitude}
                address={selectedImage.address}
              />
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default ViewFuelDetails;
